/**
 * EmployeeAccountTab — F12.M Etap D Part 2 (Employees fullscreen, Etap 1.3).
 *
 * Zakładka „Konto" — akcje na powiązanym koncie użytkownika (`employees.account`).
 *
 * Przypadki:
 *  - Pracownik **bez konta** → przycisk „Utwórz konto" (otwiera modal w parencie).
 *  - Pracownik **z kontem** → info (email + status badge) + akcje:
 *      1) „Wyślij link resetu hasła" → POST /api/users/:userId/trigger-password-reset
 *      2) „Wyloguj ze wszystkich sesji" → POST /api/users/:userId/logout-all
 *      3) „Zawieś konto" / „Aktywuj konto" → POST /api/users/:userId/suspend|activate
 *      4) „Ustaw hasło tymczasowe" / „Zmień e-mail logowania" → przycisk-link
 *         do strony Użytkownicy (Etap 1.4 — UserDetail; tu placeholder z tooltip).
 *
 * Wszystkie akcje przez ConfirmDialog (potwierdzenie destrukcyjnej operacji).
 * Permission gate: backend (każdy endpoint sprawdza `crm_core.users.*`).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { KeyRound, LogOut, Mail, ShieldOff, UserCheck, UserPlus } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { EMPLOYEE_DETAIL_KEY, type EmployeeDetailAccount } from '@/lib/use-tenant-people';

interface Props {
  employeeId: string;
  account: EmployeeDetailAccount | null;
  isDeleted: boolean;
  /** Otwiera modal „Utwórz konto" w parencie (EmployeeDetail). */
  onOpenCreateAccount: () => void;
}

type ConfirmAction =
  | { kind: 'triggerReset' }
  | { kind: 'logoutAll' }
  | { kind: 'suspend' }
  | { kind: 'activate' };

export function EmployeeAccountTab({ employeeId, account, isDeleted, onOpenCreateAccount }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  const triggerResetM = useMutation({
    mutationFn: () => api.post(`/users/${account!.userId}/trigger-password-reset`),
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.employees.account.toast.resetLinkSent') });
    },
    onError: (err) => handleAccountError(err, t, toast),
  });

  const logoutAllM = useMutation({
    mutationFn: () => api.post(`/users/${account!.userId}/logout-all`),
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.employees.account.toast.loggedOut') });
    },
    onError: (err) => handleAccountError(err, t, toast),
  });

  const suspendM = useMutation({
    mutationFn: () => api.post(`/users/${account!.userId}/suspend`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EMPLOYEE_DETAIL_KEY(employeeId) });
      toast.show({ variant: 'success', title: t('tenant.employees.account.toast.suspended') });
    },
    onError: (err) => handleAccountError(err, t, toast),
  });

  const activateM = useMutation({
    mutationFn: () => api.post(`/users/${account!.userId}/activate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EMPLOYEE_DETAIL_KEY(employeeId) });
      toast.show({ variant: 'success', title: t('tenant.employees.account.toast.activated') });
    },
    onError: (err) => handleAccountError(err, t, toast),
  });

  if (!account) {
    return (
      <div className="p-6" data-testid="employee-account-tab-empty">
        <div className="rounded-lg border border-dashed border-border-secondary p-8 text-center">
          <p className="text-sm text-text-tertiary">{t('tenant.employees.account.empty')}</p>
          {!isDeleted && (
            <Button
              variant="primary"
              size="sm"
              leftIcon={<UserPlus size={14} aria-hidden="true" />}
              onClick={onOpenCreateAccount}
              data-testid="employee-account-create-btn"
              className="mt-4"
            >
              {t('tenant.employees.actions.createAccount')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  const isSuspended = account.status === 'suspended';

  function getConfirmConfig(action: ConfirmAction): {
    title: string;
    message: string;
    variant: 'danger' | 'primary';
    confirmLabel: string;
  } {
    switch (action.kind) {
      case 'triggerReset':
        return {
          title: t('tenant.employees.account.confirm.triggerReset.title'),
          message: t('tenant.employees.account.confirm.triggerReset.message', {
            email: account!.email,
          }),
          variant: 'primary',
          confirmLabel: t('tenant.employees.account.actions.triggerReset'),
        };
      case 'logoutAll':
        return {
          title: t('tenant.employees.account.confirm.logoutAll.title'),
          message: t('tenant.employees.account.confirm.logoutAll.message'),
          variant: 'primary',
          confirmLabel: t('tenant.employees.account.actions.logoutAll'),
        };
      case 'suspend':
        return {
          title: t('tenant.employees.account.confirm.suspend.title'),
          message: t('tenant.employees.account.confirm.suspend.message'),
          variant: 'danger',
          confirmLabel: t('tenant.employees.account.actions.suspend'),
        };
      case 'activate':
        return {
          title: t('tenant.employees.account.confirm.activate.title'),
          message: t('tenant.employees.account.confirm.activate.message'),
          variant: 'primary',
          confirmLabel: t('tenant.employees.account.actions.activate'),
        };
    }
  }

  function runAction(action: ConfirmAction) {
    switch (action.kind) {
      case 'triggerReset':
        triggerResetM.mutate();
        break;
      case 'logoutAll':
        logoutAllM.mutate();
        break;
      case 'suspend':
        suspendM.mutate();
        break;
      case 'activate':
        activateM.mutate();
        break;
    }
    setConfirmAction(null);
  }

  return (
    <div className="space-y-4 p-4" data-testid="employee-account-tab">
      <div className="rounded-lg border border-border-secondary bg-bg-primary p-5">
        <div className="mb-3 flex items-center gap-2 text-xs font-medium text-text-secondary">
          <KeyRound size={12} aria-hidden="true" />
          {t('tenant.employees.account.heading')}
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-text-tertiary">
              {t('tenant.employees.account.email')}
            </span>
            <span className="text-sm font-medium" data-testid="employee-account-email">
              {account.email}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-text-tertiary">
              {t('tenant.employees.account.status')}
            </span>
            <Badge
              color={
                account.status === 'active'
                  ? 'green'
                  : account.status === 'suspended'
                    ? 'red'
                    : 'gray'
              }
            >
              {t(`tenant.users.status.${account.status}`)}
            </Badge>
          </div>
        </div>
      </div>

      {!isDeleted && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Button
            variant="secondary"
            leftIcon={<Mail size={14} aria-hidden="true" />}
            onClick={() => setConfirmAction({ kind: 'triggerReset' })}
            disabled={triggerResetM.isPending}
            data-testid="employee-account-trigger-reset"
          >
            {t('tenant.employees.account.actions.triggerReset')}
          </Button>
          <Button
            variant="secondary"
            leftIcon={<LogOut size={14} aria-hidden="true" />}
            onClick={() => setConfirmAction({ kind: 'logoutAll' })}
            disabled={logoutAllM.isPending}
            data-testid="employee-account-logout-all"
          >
            {t('tenant.employees.account.actions.logoutAll')}
          </Button>
          {isSuspended ? (
            <Button
              variant="secondary"
              leftIcon={<UserCheck size={14} aria-hidden="true" />}
              onClick={() => setConfirmAction({ kind: 'activate' })}
              disabled={activateM.isPending}
              data-testid="employee-account-activate"
            >
              {t('tenant.employees.account.actions.activate')}
            </Button>
          ) : (
            <Button
              variant="secondary"
              leftIcon={<ShieldOff size={14} aria-hidden="true" />}
              onClick={() => setConfirmAction({ kind: 'suspend' })}
              disabled={suspendM.isPending}
              data-testid="employee-account-suspend"
            >
              {t('tenant.employees.account.actions.suspend')}
            </Button>
          )}
          <Button
            variant="secondary"
            disabled
            title={t('tenant.employees.account.advancedHint')}
            data-testid="employee-account-advanced-placeholder"
          >
            {t('tenant.employees.account.actions.advanced')}
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={confirmAction !== null}
        title={confirmAction ? getConfirmConfig(confirmAction).title : ''}
        message={confirmAction ? getConfirmConfig(confirmAction).message : ''}
        variant={confirmAction ? getConfirmConfig(confirmAction).variant : 'danger'}
        confirmLabel={confirmAction ? getConfirmConfig(confirmAction).confirmLabel : undefined}
        onConfirm={() => confirmAction && runAction(confirmAction)}
        onClose={() => setConfirmAction(null)}
      />
    </div>
  );
}

function handleAccountError(
  err: unknown,
  t: ReturnType<typeof useTranslation>['t'],
  toast: ReturnType<typeof useToast>,
): void {
  if (isAxiosError(err)) {
    const code = err.response?.data?.error as string | undefined;
    if (code) {
      const msg = t(`tenant.employees.account.toast.errors.${code}`, { defaultValue: '' });
      if (msg) {
        toast.show({ variant: 'error', title: msg });
        return;
      }
    }
  }
  toast.show({ variant: 'error', title: t('tenant.employees.account.toast.error') });
}
